export interface PaginatedResponse<T> {
  content: T[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
  first: boolean;
}
export interface AustrianPubMedSearchResponse extends PaginatedResponse<AustrianPubMedArticle> {
  content: AustrianPubMedArticle[];
}
// Base interaction interface that matches the AbstractInteraction from backend
export interface AbstractInteraction {
  id?: number;
  hearticle: {
      id: number;
  };
  interactionType: InteractionType;
  interactionDate: string;
  sharePlatform?: SharePlatform;
  timeSpentSeconds?: number;
}

// Specific interaction types extending the base
export interface UserHearticleInteraction extends AbstractInteraction {
  user: {
      id: number;
      username: string;
  };
}

export interface AnonymousInteraction extends AbstractInteraction {
  sessionId: string;
  visitorId?: string;
}

// DTO for creating/sending interactions
export interface InteractionDTO {
  hearticleId: number;
  interactionType: InteractionType;
  sharePlatform?: SharePlatform;
  timeSpentSeconds?: number;
}

// Statistics interfaces
export interface InteractionStatistics {
  totalInteractions: number;
  interactionsByType: {
      [key in InteractionType]: number;
  };
  sharesByPlatform: {
      [key in SharePlatform]: number;
  };
  avgTimeSpentSeconds: number;
}

export enum InteractionType {
  READ = 'READ',
  LIKE = 'LIKE',
  BOOKMARK = 'BOOKMARK',
  SHARE = 'SHARE',
  COMMENT = 'COMMENT',
  FOLLOW_TOPIC = 'FOLLOW_TOPIC',
  PRINT = 'PRINT',
  DOWNLOAD = 'DOWNLOAD',
  VIEW_RELATED = 'VIEW_RELATED',
  REPORT = 'REPORT'
}

export enum SharePlatform {
  FACEBOOK = 'FACEBOOK',
  LINKEDIN = 'LINKEDIN',
  X = 'X',
  INSTAGRAM = 'INSTAGRAM',
  WHATSAPP = 'WHATSAPP',
  EMAIL = 'EMAIL'
}
export interface Journal {
  name: string;
  aiGeneratedScore?: number;
  impactFactor?: number;
}
// Rest of your existing interfaces remain the same
export interface HearticleTranslation {
  id: number;
  languageCode: string;
  headline: string;
  keywords: string[];
  mainTextBody: string;
  mainActions: string[];
  harmfulActions: string[];
  hearticle?: Hearticle;
}
export interface AustrianPubMedArticle {
  pmid: string;
  title: string;
  abstractText: string;
  url: string;
  authors: string[];
  publicationDate: string;
  journal: Journal;
  translations: PubMedArticleTranslation[];
  rubric?: Rubric;
}

export interface PubMedArticleTranslation {
  id: number;
  languageCode: string;
  translatedTitle: string;
  translatedAbstract: string;
  keywords?: string[];
  pubMedArticle?: PubMedArticle; // Optional as it's marked @JsonIgnore in backend
}

export interface PubMedSearchResponse {
  content: PubMedArticle[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
  first: boolean;
}
export interface PubMedSearchSuggestion {
  pmid: string;
  title: string;
  authors: string[];
  journal: string;
  publicationDate: string;
}
export interface PubMedSearchParams {
  searchTerm: string;
  languageCode: string;
  page: number;
  size: number;
}

export interface Rubric {
  id: number;
  name: string;
  description: string;
}

export interface PubMedArticle {
  pmid: string;
  title: string;
  abstractText: string;
  url: string;
  authors: string[];
  publicationDate: string;
  displayDate: string;
  displayed: boolean;
  citationCount: number;
  journal: string;
  keywords: string[];
  reputabilityScore: number;
  impactFactor: number | null;
  publicationTypes: string[];
  translations: PubMedArticleTranslation[];
  topic?: Topic;
  embeddings?: number[]; // Optional as it's marked @JsonIgnore in backend
}

export interface Topic {
  id: number;
  name: string;
  imagePath: string;
  additionalInfo: string;
  statusCode: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  subTopics: string[];
  color: string;
  rubric?: Rubric
}

export interface Rubric {
  id: number;
  name: string;
}

export interface Hearticle {
  id: number;
  creationDate: string;
  publicationDate: string;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  searchPhrases: string[];
  relatedPubMedArticles: PubMedArticle[];
  translations: HearticleTranslation[];
  topic: HearticleTopic;
}

export interface HearticleTopic {
  id: number;
  name: string;
  imagePath: string;
  additionalInfo: string;
  statusCode: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  subTopics: string[];
  color: string;
}

export interface Review {
  id: number;
  hearticleTranslation: HearticleTranslation;
  reviewer: {
      id: string;
      username?: string;
      email?: string;
  };
  reviewDateTime: string;
  comments: string;
  status: ReviewStatus;
  changes: ReviewChange[];
}

export type ReviewStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'NEEDS_REVISION';

export type ChangeField = 'HEADLINE' | 'KEYWORD' | 'MAIN_TEXT_BODY' | 'MAIN_ACTION';

export interface ReviewChange {
  id?: number;
  field: ChangeField;
  previousValue: string;
  newValue: string;
  listIndex?: number;
}
